/**
 * `encode/binary` — hex dump and parser.
 *
 * Three-column dump (offset | hex bytes | ASCII gutter), in the
 * format that `xxd` / `hexdump -C` produce. Layout is:
 *
 *   00000000  48 65 6c 6c 6f 2c 20 77  6f 72 6c 64 21 0a 00 00  |Hello, world!...|
 *
 * Columns are configurable (8 / 16 / 32) and case is configurable.
 *
 * The ASCII gutter prints printable ASCII verbatim and replaces
 * everything else (including DEL / control bytes / non-ASCII high
 * bits) with `.`. We intentionally do NOT decode UTF-8 in the
 * gutter — this is a binary view, not a text view.
 *
 * `parseHexDump` is permissive: it accepts xxd / hexdump -C output,
 * a raw stream of hex bytes ('48 65 6c…'), or even the ASCII gutter
 * inlined (`|Hello…|` is recognised and dropped). It rejects any
 * line whose hex column is malformed.
 */

export interface HexDumpOpts {
  columns?: 8 | 16 | 32;
  uppercase?: boolean;
  /**
   * Include the offset column (default true). Disabling it produces
   * just `<hex bytes>  |<ascii>|` lines, useful when the user wants
   * to copy a fragment.
   */
  showOffset?: boolean;
  /** Include the ASCII gutter (default true). */
  showAscii?: boolean;
}

export interface ParsedHex {
  bytes: Uint8Array;
  /** Lines we couldn't parse — surfaced in the UI as warnings. */
  errors: Array<{ line: number; reason: string }>;
}

const PRINTABLE_LO = 0x20;
const PRINTABLE_HI = 0x7e;

function asciiByte(b: number): string {
  return b >= PRINTABLE_LO && b <= PRINTABLE_HI ? String.fromCharCode(b) : '.';
}

export function hexDump(bytes: Uint8Array, opts: HexDumpOpts = {}): string {
  const cols = opts.columns ?? 16;
  const uc = !!opts.uppercase;
  const showOffset = opts.showOffset !== false;
  const showAscii = opts.showAscii !== false;
  if (bytes.byteLength === 0) {
    // Still emit a single empty-row line so the user sees the
    // formatting they're going to get.
    return showOffset ? '00000000\n' : '\n';
  }

  const lines: string[] = [];
  for (let off = 0; off < bytes.length; off += cols) {
    const slice = bytes.subarray(off, Math.min(off + cols, bytes.length));

    // hex column: `xx xx xx ...` with a double space at the half-way
    // mark to mimic xxd / hexdump -C readability.
    const hexParts: string[] = [];
    for (let i = 0; i < cols; i++) {
      if (i < slice.length) {
        const h = slice[i].toString(16).padStart(2, '0');
        hexParts.push(uc ? h.toUpperCase() : h);
      } else {
        hexParts.push('  ');
      }
    }
    let hexCol: string;
    if (cols >= 16) {
      const half = cols / 2;
      hexCol = hexParts.slice(0, half).join(' ') + '  ' + hexParts.slice(half).join(' ');
    } else {
      hexCol = hexParts.join(' ');
    }

    const offCol = off.toString(16).padStart(8, '0');
    const offStr = uc ? offCol.toUpperCase() : offCol;

    let asciiCol = '';
    if (showAscii) {
      const printed = Array.from(slice).map(asciiByte).join('');
      const padded = printed.padEnd(cols, ' ');
      asciiCol = `|${padded}|`;
    }

    const parts: string[] = [];
    if (showOffset) parts.push(offStr);
    parts.push(hexCol);
    if (showAscii) parts.push(asciiCol);
    lines.push(parts.join('  '));
  }
  return lines.join('\n') + '\n';
}

/**
 * Permissive hex-dump → bytes parser. Accepts:
 *
 *   - `xxd` / `hexdump -C` output (offset + hex + ascii gutter).
 *   - A raw run of space-separated hex pairs.
 *   - A continuous string of hex digits (`48656c6c6f`).
 *
 * The strategy is to drop the offset column (any leading
 * `^[0-9a-fA-F]{4,}\s+`), drop the trailing ASCII gutter (anything
 * after `|...|` or after two-or-more spaces near the end of the
 * line — the latter handles `xxd -p` output too), and parse what's
 * left as hex pairs.
 */
export function parseHexDump(text: string): ParsedHex {
  const errors: Array<{ line: number; reason: string }> = [];
  const collected: number[] = [];

  const lines = text.split(/\r?\n/);
  for (let li = 0; li < lines.length; li++) {
    let line = lines[li];
    if (!line.trim()) continue;

    // Drop trailing ASCII gutter: |Hello...|
    line = line.replace(/\|[^|]*\|\s*$/, '');

    // Drop leading offset (8+ hex digits followed by whitespace or
    // colon — handles `00000000:` and `0000  ` styles).
    line = line.replace(/^\s*[0-9a-fA-F]{4,}[:\s]\s*/, '');

    // xxd-style output sometimes has the ASCII column inlined at the
    // tail of the line (separated by 3+ spaces) without the `|...|`
    // delimiters. Cut everything after the first ≥3-space run.
    // Threshold ≥3 (not ≥2) so the conventional 2-space half-split
    // inside `hexdump -C` lines (`xx xx  xx xx`) is preserved.
    const sep = line.search(/\s{3,}/);
    if (sep !== -1) line = line.slice(0, sep);

    const hexOnly = line.replace(/[\s,]+/g, '');
    if (!hexOnly) continue;
    if (!/^[0-9a-fA-F]+$/.test(hexOnly)) {
      errors.push({ line: li + 1, reason: '含非 hex 字符' });
      continue;
    }
    if (hexOnly.length % 2 !== 0) {
      errors.push({ line: li + 1, reason: 'hex 字符串长度为奇数' });
      continue;
    }
    for (let j = 0; j < hexOnly.length; j += 2) {
      collected.push(parseInt(hexOnly.slice(j, j + 2), 16));
    }
  }

  return { bytes: new Uint8Array(collected), errors };
}
